import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';
import KnowledgeBaseTable from './KnowledgeBaseTable.jsx';
import KnowledgeBaseDetailsPanel from './KnowledgeBaseDetailsPanel.jsx';
import KnowledgeBaseFilters from './KnowledgeBaseFilters.jsx';
import KnowledgeBaseAssignmentModule from '../../modules/knowledge-base-assignment/KnowledgeBaseAssignmentModule.jsx';
import { ModuleContextProvider } from '../../modules/context/ModuleContextProvider.jsx';
import KnowledgeBaseUploadModal from './KnowledgeBaseUploadModal.jsx';
import KnowledgeBaseContentEditor from './KnowledgeBaseContentEditor.jsx';
import KnowledgeBaseDeletionModal from './KnowledgeBaseDeletionModal.jsx';
import KnowledgeBaseMetricsPanel from './KnowledgeBaseMetricsPanel.jsx';
import { useToast } from '../../contexts/ToastContext.jsx';
import { useTheme } from '../../contexts/ThemeContext.jsx';
import { ApiError } from '../../utils/apiClient.js';
import { parseApiError } from '../../utils/consolidatedErrorHandling.js';
import { apiRequest } from '../../utils/apiClient.js';

/**
 * Main Knowledge Base management component for admin interface
 */
const KnowledgeBaseManagement = () => {
  const { getToken } = useAuth();
  const { showToast } = useToast();
  const { theme } = useTheme();
  
  // State management
  const [knowledgeBaseFiles, setKnowledgeBaseFiles] = useState([]);
  const [selectedKB, setSelectedKB] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    user: '',
    hasAssociations: 'all',
    sortBy: 'updated_at',
    sortOrder: 'desc'
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
    sortBy: 'updated_at',
    sortOrder: 'desc'
  });

  // Modal states
  const [showAssociationManager, setShowAssociationManager] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showContentEditor, setShowContentEditor] = useState(false);
  const [showDeletionModal, setShowDeletionModal] = useState(false);
  const [showMetricsPanel, setShowMetricsPanel] = useState(false);

  /**
   * Fetch Knowledge Base files from API with current filters and pagination
   */
  const fetchKnowledgeBaseFiles = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = await getToken();
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        limit: pagination.limit.toString(),
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder
      });

      // Add optional filters
      if (filters.search) params.append('search', filters.search);
      if (filters.status !== 'all') params.append('status', filters.status);
      if (filters.user) params.append('user', filters.user);
      if (filters.hasAssociations !== 'all') params.append('hasAssociations', filters.hasAssociations);

      const response = await apiRequest(`/api/admin/knowledge-base?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorMessage = await parseApiError(response);
        throw new ApiError(errorMessage, response.status, response);
      }

      const data = await response.json();
      setKnowledgeBaseFiles(data.knowledgeBaseFiles || []);
      setPagination(prev => ({
        ...prev,
        total: data.pagination?.total || 0,
        totalPages: data.pagination?.pages || 0,
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder
      }));

    } catch (error) {
      console.error('Error fetching Knowledge Base files:', error);
      setError(error.message || 'Failed to load Knowledge Base files');
      showToast('Failed to load Knowledge Base files', 'error');
    } finally {
      setLoading(false);
    }
  }, [getToken, filters, pagination.page, pagination.limit, showToast]);

  /**
   * Handle filter changes
   */
  const handleFiltersChange = useCallback((newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page
  }, []);

  /**
   * Handle pagination changes
   */
  const handlePageChange = useCallback((newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  }, []);

  /**
   * Handle Knowledge Base selection
   */
  const handleKBSelect = useCallback((kb) => {
    setSelectedKB(kb);
  }, []);

  /**
   * Handle Knowledge Base updates
   */
  const handleKBUpdate = useCallback((updatedKB) => {
    setKnowledgeBaseFiles(prev => prev.map(kb => 
      kb.id === updatedKB.id ? updatedKB : kb
    ));
    setSelectedKB(updatedKB);
    showToast('Knowledge Base file updated successfully', 'success');
  }, [showToast]);

  /**
   * Open association manager modal
   */
  const openAssociationManager = useCallback((kb) => {
    setSelectedKB(kb);
    setShowAssociationManager(true);
  }, []);

  /**
   * Handle association updates
   */
  const handleAssociationUpdate = useCallback(() => {
    // Refresh the Knowledge Base files list to get updated association counts
    fetchKnowledgeBaseFiles();
  }, [fetchKnowledgeBaseFiles]);

  /**
   * Handle upload completion
   */
  const handleUploadComplete = useCallback(() => {
    fetchKnowledgeBaseFiles();
    setShowUploadModal(false);
  }, [fetchKnowledgeBaseFiles]);

  /**
   * Handle Knowledge Base deletion
   */
  const handleKBDelete = useCallback((deletedKB) => {
    setKnowledgeBaseFiles(prev => prev.filter(kb => kb.id !== deletedKB.id));
    setSelectedKB(null);
    setShowDeletionModal(false);
  }, []);

  /**
   * Open content editor
   */
  const openContentEditor = useCallback((kb) => {
    setSelectedKB(kb);
    setShowContentEditor(true);
  }, []);

  /**
   * Open deletion modal
   */
  const openDeletionModal = useCallback((kb) => {
    setSelectedKB(kb);
    setShowDeletionModal(true);
  }, []);

  /**
   * Open metrics panel
   */
  const openMetricsPanel = useCallback((kb) => {
    setSelectedKB(kb);
    setShowMetricsPanel(true);
  }, []);

  // Load Knowledge Base files on component mount and when dependencies change
  useEffect(() => {
    fetchKnowledgeBaseFiles();
  }, [fetchKnowledgeBaseFiles]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Knowledge Base Management</h1>
          <p className={`mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            Manage knowledge base files and their associations across all users
          </p>
        </div>
        <div className="flex items-center space-x-4">
          <button
            onClick={() => setShowUploadModal(true)}
            className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors flex items-center space-x-2 dark:text-white"
          >
            <span>+</span>
            <span>Upload Knowledge Base Files</span>
          </button>
          <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            {pagination.total > 0 && (
              <span>
                Showing {((pagination.page - 1) * pagination.limit) + 1}-{Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} files
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Error state */}
      {error && (
        <div className={`rounded-lg p-4 border ${
          theme === 'dark'
            ? 'bg-red-900/20 border-red-500/50'
            : 'bg-red-50 border-red-200'
        }`}>
          <div className="flex items-center">
            <span className={`mr-2 ${theme === 'dark' ? 'text-red-400' : 'text-red-600'}`}>⚠️</span>
            <span className={theme === 'dark' ? 'text-red-300' : 'text-red-700'}>{error}</span>
            <button
              onClick={fetchKnowledgeBaseFiles}
              className={`ml-auto underline transition-colors ${
                theme === 'dark'
                  ? 'text-red-400 hover:text-red-300'
                  : 'text-red-600 hover:text-red-700'
              }`}
            >
              Retry
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Filters sidebar */}
        <div className="lg:col-span-1">
          <KnowledgeBaseFilters
            filters={filters}
            onChange={handleFiltersChange}
            loading={loading}
          />
        </div>

        {/* Main content */}
        <div className="lg:col-span-3 space-y-6">
          {/* Knowledge Base files table */}
          <KnowledgeBaseTable
            knowledgeBaseFiles={knowledgeBaseFiles}
            loading={loading}
            selectedKB={selectedKB}
            onKBSelect={handleKBSelect}
            pagination={pagination}
            onPageChange={handlePageChange}
            onSort={(sortBy, sortOrder) => handleFiltersChange({ sortBy, sortOrder })}
            onViewAssociations={openAssociationManager}
          />

          {/* Knowledge Base details panel */}
          {selectedKB && !showAssociationManager && !showContentEditor && !showDeletionModal && !showMetricsPanel && (
            <KnowledgeBaseDetailsPanel
              knowledgeBase={selectedKB}
              onUpdate={handleKBUpdate}
              onViewAssociations={openAssociationManager}
              onEditContent={openContentEditor}
              onDelete={openDeletionModal}
              onViewMetrics={openMetricsPanel}
              onClose={() => setSelectedKB(null)}
            />
          )}
        </div>
      </div>

      {/* Modals */}
      <KnowledgeBaseUploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onUploadComplete={handleUploadComplete}
      />

      {showAssociationManager && selectedKB && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Manage Assignments - {selectedKB.name}
              </h2>
              <button
                onClick={() => setShowAssociationManager(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 dark:text-gray-300 dark:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              <ModuleContextProvider>
                <KnowledgeBaseAssignmentModule
                  config={{
                    context: 'admin-kb',
                    mode: 'batch-assign',
                    securityLevel: 'admin',
                    showSyncStatus: true,
                    allowBatchOperations: true,
                    enableRealTimeUpdates: true,
                    enableAssignmentHistory: true,
                    showConflictResolution: true,
                    onAssignmentChange: () => {
                      handleAssociationUpdate();
                    }
                  }}
                  files={[selectedKB]}
                />
              </ModuleContextProvider>
            </div>
          </div>
        </div>
      )}

      <KnowledgeBaseContentEditor
        knowledgeBase={selectedKB}
        isOpen={showContentEditor}
        onClose={() => setShowContentEditor(false)}
        onSave={handleKBUpdate}
      />

      <KnowledgeBaseDeletionModal
        knowledgeBase={selectedKB}
        isOpen={showDeletionModal}
        onClose={() => setShowDeletionModal(false)}
        onDelete={handleKBDelete}
      />

      <KnowledgeBaseMetricsPanel
        knowledgeBase={selectedKB}
        isOpen={showMetricsPanel}
        onClose={() => setShowMetricsPanel(false)}
      />
    </div>
  );
};

export default KnowledgeBaseManagement;
